import React, { Component } from 'react';
import { merge, contains, pathOr } from 'ramda';
import { TENANT_VIEWS } from './tenantPages';
import SearchInput from '../SearchInput';
import UserSources from '../../sources/user';
import { bindMethods, stringReplace } from '../../utils/core';
import PagedTable from '../PagedTable';
import { addNotification } from '../../actions/reduxActions/notification';
import { notificationTypes, SYSTEM_ROLES } from '../../constants';
import { connect } from 'react-redux';
import translations from '../../translations';
import Confirm from '../generic/confirm';

export class UserList extends Component {

    state = {
        users: [],
        showRemoveUserConfirm: false,
        showUpdateRoleConfirm: false,
        selectedUser: null,

        /**
            {
                user: { developerName: "Foo"... } // The updated user metadata used in request payload
                currentRole: "Administrator",
                newRole: "Standard User",
            }
         */
        updatedUser: null,
        removingUser: false,
        isLoading: false,
        paging: {
            page: 1,
            pageSize: 10,
            total: 0,
        },
        query: '',
    };

    constructor(props) {
        super(props);

        bindMethods(
            this,
            'onPage',
            'onSearch',
            'hideRemoveUserModal',
            'onRemoveUserConfirmed',
            'hideUpdateRoleModal',
        );
    }

    componentDidMount() {
        this.fetchAndUpdateUsers();
    }

    onSearch(query) {
        this.onPage(1);
        this.setState({
            query,
        });
    }

    onPage(pageNumber) {

        const paging = merge(this.state.paging, { page: pageNumber });

        this.setState({
            paging,
        });
    }

    onRemoveUser(user) {

        this.setState({
            selectedUser: user,
            showRemoveUserConfirm: true,
        });
    }

    onUserRoleChange = async (userId, newRole) => {
        try {
            const { authenticationToken, tenantId } = this.props;
            const userToUpdate = await UserSources.getTenantUser({ authenticationToken, tenantId, userId });
            const role = Object.values(SYSTEM_ROLES).find(sr => sr.developerName === newRole);

            const updatedUser = {
                user: { ...userToUpdate, role }, // Set the new role for the user
                currentRole: userToUpdate.role.friendlyName,
                newRole: role.friendlyName,
            };

            const users = this.updateUsersToDisplay(userId, updatedUser.user);

            this.setState({
                users,
                updatedUser,
                showUpdateRoleConfirm: true,
            });

        } catch ({ message }) {
            this.props.addNotification({
                type: notificationTypes.ERROR,
                message,
                isPersistent: true,
            });
        }
    }

    onRemoveUserConfirmed() {

        this.setState({
            removingUser: true,
        });

        this.removeUserFromTenant(this.state.selectedUser.id)
            .then(() => {
                this.setState({
                    selectedUser: null,
                    showRemoveUserConfirm: false,
                    removingUser: false,
                });
                this.fetchAndUpdateUsers();
            })
            .catch((error) => {
                this.props.addNotification({
                    type: notificationTypes.ERROR,
                    message: error.message,
                    isPersistent: true,
                });
            });


    }

    updateRole = async () => {
        try {
            const { authenticationToken, tenantId } = this.props;
            const { user } = this.state.updatedUser;
            
            await UserSources.updateTenantUser({ authenticationToken, tenantId, userId: user.id, userPayload: user });

            const users = this.updateUsersToDisplay(user.id, user);

            this.props.addNotification({
                type: notificationTypes.SUCCESS,
                message: 'The user\'s role has been successfully updated',
                isPersistent: false,
            });

            this.setState({
                users,
                updatedUser: null,
                showUpdateRoleConfirm: false,
            });

        } catch ({ message }) {
            this.props.addNotification({
                type: notificationTypes.ERROR,
                message,
                isPersistent: true,
            });
        }
    }

    // Update the user's role in component state.
    // This is so that the role select box has the correct selection
    updateUsersToDisplay = (id, user) => this.state.users.map(u => u.id === id ? user : u);

    fetchUsers() {
        const { authenticationToken, tenantId } = this.props;
        return UserSources.getAllUsersForCurrentTenant({ authenticationToken, tenantId });
    }

    fetchAndUpdateUsers() {
        this.fetchUsers()
            .then(
                (users) => {

                    const paging = merge(this.state.paging, { total: users.length });

                    this.setState({
                        users,
                        paging,
                    });
                },
            );
    }

    removeUserFromTenant(id) {
        return UserSources.removeUserFromTenant({ authenticationToken: this.props.authenticationToken, tenantId: this.props.tenantId, id });
    }

    hideRemoveUserModal() {
        this.setState({ showRemoveUserConfirm: false });
    }

    hideUpdateRoleModal() {
        this.setState({ showUpdateRoleConfirm: false, updatedUser: null });

        // This is so the role select box get repopulated with the
        // correct role selection if the role update is cancelled
        this.fetchAndUpdateUsers();
    }

    render() {

        const userColumns = [
            {
                renderHeader: () => 'Actions',
                renderCell: ({ item: user }) => (
                    <button className="btn-icon danger" onClick={() => this.onRemoveUser(user)}>
                        <span className="glyphicon glyphicon-trash" />
                    </button>
                ),
                size: '1px',
            },
            {
                propertyName: 'email',
                renderHeader: () => 'Email',
            },
            {
                propertyName: 'firstName',
                renderHeader: () => 'First Name',
            },
            {
                propertyName: 'lastName',
                renderHeader: () => 'Last Name',
            },
            {
                propertyName: 'role',
                renderCell: ({ item: { role, id } }) => {
                    const currentRoleDeveloperName = pathOr('', ['developerName'], role);
                    return [
                        <select
                            onChange={({ target }) => this.onUserRoleChange(id, target.value)}
                            value={currentRoleDeveloperName}
                        >
                            {Object.keys(SYSTEM_ROLES).filter(sr => sr !== 'service_user').map(sr => (
                                <option key={SYSTEM_ROLES[sr].roleId} value={SYSTEM_ROLES[sr].developerName}>
                                    {SYSTEM_ROLES[sr].friendlyName}
                                </option>
                            ))}
                        </select>,
                    ];
                },
                renderHeader: () => 'Role',
            },
            {
                propertyName: 'isSso',
                renderCell: ({ item: { isSso } }) => [
                    <span>{isSso ? 'Yes' : ''}</span>,
                ],
                renderHeader: () => 'Single Sign-On',
            },
        ];

        const { users, query, paging: { page, pageSize } } = this.state;
        const containsQuery = contains(query.toLowerCase());

        const queriedUsers = users.filter(
            user => !query
                || (user.firstName && containsQuery(user.firstName.toLowerCase()))
                || (user.lastName && containsQuery(user.lastName.toLowerCase()))
                || containsQuery(user.email.toLowerCase()),
        );

        const { firstName, lastName, email } = pathOr({}, ['state', 'selectedUser'], this);
        const { currentRole, user: updatedUser, newRole } = pathOr({}, ['state', 'updatedUser'], this);

        return (
            <React.Fragment>

                <Confirm
                    show={this.state.showRemoveUserConfirm}
                    title={translations.TENANT_remove_confirmation_title}
                    messages={[
                        stringReplace(translations.TENANT_remove_confirmation_message, {
                            firstName,
                            lastName,
                            email,
                        }),
                    ]}
                    buttonStyle="danger"
                    buttonCaption="Remove"
                    onCancel={this.hideRemoveUserModal}
                    onConfirm={this.onRemoveUserConfirmed}
                    isInProgress={this.state.removingUser}
                />

                <Confirm
                    show={this.state.showUpdateRoleConfirm}
                    title={translations.TENANT_update_role_title}
                    messages={[
                        stringReplace(translations.TENANT_update_role_message, {
                            currentRole,
                            email: updatedUser ? updatedUser.email : null,
                            newRole,
                        }),
                    ]}
                    buttonCaption="Assign new role"
                    onCancel={this.hideUpdateRoleModal}
                    onConfirm={this.updateRole}
                />

                <div className="flex">
                    <button onClick={() => this.props.navigateTo(TENANT_VIEWS.userDetails)} className="btn btn-sm btn-success">
                        <span className="glyphicon glyphicon-plus" />
                        {
                            ' Add User'
                        }
                    </button>
                    <SearchInput onSearch={this.onSearch} className="margin-left" />
                </div>
                
                <PagedTable
                    className="table margin-top"
                    columns={userColumns}
                    items={queriedUsers}
                    isLoading={this.state.isLoading}
                    page={page}
                    pageSize={pageSize}
                    onPageClick={this.onPage}
                />
                <a
                    className="link"
                    rel="noopener noreferrer"
                    target="_blank" 
                    href="https://help.boomi.com/csh?context=GUID-ad679279-a8fd-4b6e-bccc-e99bbd2c3c05">
                        See role access information
                </a>
            </React.Fragment>
        );
    }
}

export default connect(
    null,
    { addNotification },
)(UserList);
